import { Button, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import React, { useState } from 'react';
import { moduleManagementService } from 'services/dacum/module.service';
import FilterModuleCompetency from './FilterModuleCompetency';
import CreateModuleJobFamily from './CreateModuleJobFamily';
import ModalDelete from 'modules/pages/lcms/courses/ModalDelete';
import { IconPuzzle, IconPlus } from '@tabler/icons-react';
import { column } from './schema';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const TableModuleManagementJobFamily = () => {
  const [selectedModule, setSelectedModule] = useState({
    value: '',
    label: ''
  });
  const [params, setParams] = useState({
    sortOrder: 'asc',
    pageNumber: 1,
    pageSize: 10
  });

  const [isOpen, { open, close }] = useDisclosure(false);
  const [isCreateOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [isDeleteOpen, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState<any>(null);
  const queryClient = useQueryClient();

  const addJobFamily = useMutation({
    mutationFn: moduleManagementService.createModuleCompetencyJobFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['moduleCompetency', selectedModule.value, params]
      });
      closeCreate();
      showSuccessNotification({
        title: 'Success',
        message: 'Job family added successfully'
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when add job family',
        message: error.message
      });
    }
  });

  const deleteJobFamily = useMutation({
    mutationFn: moduleManagementService.deleteModuleCompetencyJobFamily,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['moduleCompetency', selectedModule.value, params]
      });
      closeDelete();
      showSuccessNotification({
        title: 'Success',
        message: 'Job family deleted successfully'
      });
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when delete job family',
        message: error.message
      });
    }
  });

  const handleConfirmDelete = () => {
    if (selectedItemToDelete?.trCompetencyModuleJobFamilyID) {
      deleteJobFamily.mutate({
        TrCompetencyModuleJobFamilyID: selectedItemToDelete.trCompetencyModuleJobFamilyID
      });
    }
    closeDelete();
  };

  const { data, isFetching } = useQuery({
    queryKey: ['moduleCompetency', selectedModule.value, params],
    queryFn: () =>
      moduleManagementService.getModuleCompetencyJobFamily({
        trCompetencyModuleID: selectedModule.value,
        sortOrder: params.sortOrder as 'asc' | 'desc',
        pageNumber: params.pageNumber,
        pageSize: params.pageSize
      }),
    retry: false,
    enabled: selectedModule.value !== ''
  });

  return (
    <>
      <Card>
        <Stack gap={'sm'}>
          <Text c='dark' fw={600}>
            {selectedModule.label}
          </Text>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button variant='outline' onClick={open} leftSection={<IconPuzzle />}>
              {!selectedModule.value ? 'Select ' : 'Change '}Module Competency
            </Button>
            {selectedModule.value && (
              <Button onClick={openCreate} leftSection={<IconPlus />}>
                Add Job Family
              </Button>
            )}
          </div>

          <DataTables
            columns={column}
            data={data?.data || []}
            loading={isFetching}
            emptyMessage='Please select module competency'
            emptyMessageDescription='To get started please select the module competency.'
            handleChangePage={(pageNumber) => {
              setParams({
                ...params,
                pageNumber
              });
            }}
            handleChangeSize={(pageSize) => {
              setParams({
                ...params,
                pageSize
              });
            }}
            handleDelete={(item) => {
              setSelectedItemToDelete(item);
              openDelete();
            }}
          />
        </Stack>
      </Card>
      <FilterModuleCompetency
        opened={isOpen}
        onClose={close}
        onSubmit={(item) => {
          setSelectedModule(item);
          close();
        }}
      />
      <CreateModuleJobFamily
        opened={isCreateOpen}
        onClose={closeCreate}
        onSubmit={(data) => {
          addJobFamily.mutate(data);
        }}
      />
      <ModalDelete
        opened={isDeleteOpen}
        onClose={closeDelete}
        onConfirm={handleConfirmDelete}
        isLoading={deleteJobFamily.isPending}
        title='Delete Job Family'
        description='Are you sure you want to delete this job family? This action cannot be undone.'
        labelConfirm='Delete Job Family'
      />
    </>
  );
};

export default TableModuleManagementJobFamily;
